import React, { useEffect, useMemo } from 'react';
import { QueryErrorAlert } from '@shared/components';
import type { ConfiguredService } from '../../../types/service.types';
import { useSmriqConfiguratorJourney } from '../../../hooks/useSmriqConfiguratorJourney';
import { SmriqVehicleForm } from './SmriqVehicleForm';
import { SmriqConfirmedVehicleSummary } from './SmriqConfirmedVehicleSummary';
import { SmriqPlanControls } from './SmriqPlanControls';
import { SmriqPlanControlsSummary } from './SmriqPlanControlsSummary';
import { SmriqSchedulePanel } from './SmriqSchedulePanel';
import { SmriqLastCompletedServicePanel } from './SmriqLastCompletedServicePanel';
import { isAutoMatch } from '../../../utils/isAutoMatch';
import { lastCompletedServiceFieldErrors } from '../../../utils/lastCompletedServiceFieldErrors';
import { isSmriqSessionExpired } from '../../../utils/smriqSessionExpired';
import { serviceOrderErrorMessages } from '../../../utils/smriqServiceOrderError';

interface SmriqConfiguratorProps {
    setConfiguredServices: React.Dispatch<React.SetStateAction<ConfiguredService[]>>;
    className?: string;
    /** Debounce before an include/decline toggle regenerates the plan. Overridable for tests. */
    autoRegenerateDebounceMs?: number;
    /** Reports the "Updating plan…" / "Retrieving plan…" state so the summary can cascade loaders
     *  with the Service Order, from the moment it starts rather than when the visits return. */
    onServiceOrderLoadingChange?: (loading: boolean) => void;
}

// Near-propless, like ServiceConfigurator: everything else is read from the journey hook,
// which owns confirm -> schedule-preview -> generate.
export const SmriqConfigurator: React.FC<SmriqConfiguratorProps> = ({
    setConfiguredServices,
    className,
    autoRegenerateDebounceMs,
    onServiceOrderLoadingChange,
}) => {
    const journey = useSmriqConfiguratorJourney({
        setConfiguredServices,
        autoRegenerateDebounceMs,
    });

    const {
        context,
        isContextLoading,
        refetchContext,
        currency,
        configuredServices,
        smriqPlanSettings,
        vehicleContext,
        defaultVrm,
        confirmedVehicle,
        confirmVehicle,
        isConfirming,
        confirmError,
        confirmed,
        needsReconfirm,
        markVehicleDirty,
        isStale,
        isGenerating,
        generateError,
        hasPlan,
        planSettingsKnownOrRestored,
        showVehicleForm,
        autoConfirmPending,
        retrievingPlan,
        planLoading,
        showPlanControls,
    } = journey;

    const lastCompleted = journey.lastCompletedService;
    const lastCompletedInPanel = confirmed && lastCompleted.supported;
    const serverErrors = useMemo(
        () => lastCompletedServiceFieldErrors(generateError, isAutoMatch(lastCompleted.selection)),
        [generateError, lastCompleted.selection],
    );

    const serviceOrderErrors = useMemo(
        () =>
            serviceOrderErrorMessages(generateError, {
                autoMatch: isAutoMatch(lastCompleted.selection),
                panelMounted: lastCompletedInPanel,
            }),
        [generateError, lastCompleted.selection, lastCompletedInPanel],
    );

    useEffect(() => {
        onServiceOrderLoadingChange?.(planLoading);
        // Reset on unmount: a loader left true would stick on the parent forever.
        return () => onServiceOrderLoadingChange?.(false);
    }, [planLoading, onServiceOrderLoadingChange]);

    return (
        <div className={className}>
            <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">1. Configure Core Services</h2>

            {showVehicleForm ? (
                <SmriqVehicleForm
                    defaultVrm={confirmedVehicle?.vrm ?? defaultVrm}
                    defaultCurrentMileage={
                        confirmedVehicle?.currentMileage ?? vehicleContext.vehicleMileage
                    }
                    defaultAnnualMileage={
                        confirmedVehicle?.annualMileage ?? vehicleContext.annualMileage
                    }
                    onConfirm={confirmVehicle}
                    onFieldsChange={markVehicleDirty}
                    allowRegistrationEdit={journey.allowRegistrationEdit}
                    onCancel={journey.cancelVehicleEdit}
                    isConfirming={isConfirming}
                    confirmError={confirmError}
                    confirmed={confirmed && !needsReconfirm}
                    className="mt-4"
                />
            ) : confirmed ? (
                <SmriqConfirmedVehicleSummary
                    className="mt-4"
                    vrm={confirmedVehicle?.vrm ?? defaultVrm}
                    currentMileage={confirmedVehicle?.currentMileage ?? vehicleContext.vehicleMileage}
                    annualMileage={confirmedVehicle?.annualMileage ?? vehicleContext.annualMileage}
                    onEdit={journey.requestVehicleEdit}
                />
            ) : autoConfirmPending || isConfirming ? (
                <div className="bg-mp-surface rounded-xl mt-4">
                    <p role="status" className="text-sm text-mp-muted">
                        Confirming the vehicle...
                    </p>
                </div>
            ) : null}

            {lastCompletedInPanel && (
                <SmriqLastCompletedServicePanel
                    className="mt-4"
                    options={lastCompleted.options}
                    optionsState={lastCompleted.optionsState}
                    nextService={lastCompleted.nextService}
                    autoMatchedService={lastCompleted.autoMatchedService}
                    warnings={lastCompleted.previewWarnings}
                    canGeneratePlan={lastCompleted.canGeneratePlan}
                    selection={lastCompleted.selection}
                    selectionValid={lastCompleted.selectionValid}
                    selectionWithdrawn={lastCompleted.selectionWithdrawn}
                    resetKey={lastCompleted.selectionResetKey}
                    planStartDate={lastCompleted.planStartDate}
                    vehicleLastServiceDate={vehicleContext.lastServiceDate}
                    vehicleLastServiceMileage={vehicleContext.lastServiceMileage}
                    serverErrors={serverErrors}
                    hasPlan={hasPlan}
                    isStale={isStale}
                    isGenerating={isGenerating}
                    sessionExpired={isSmriqSessionExpired(generateError)}
                    needsReconfirm={needsReconfirm}
                    planFieldsEdited={lastCompleted.planFieldsEdited}
                    onChange={lastCompleted.changeSelection}
                    onRetryPreview={lastCompleted.retryPreview}
                    onUpdate={lastCompleted.onUpdate}
                    expanded={lastCompleted.expanded}
                    onEdit={lastCompleted.requestEdit}
                    onCancel={lastCompleted.cancelEdit}
                />
            )}

            {/* Context only gates the editable controls; the summary below shows without it, so a
                resumed plan's parameters appear at once rather than after the context fetch. */}
            {confirmed && isContextLoading && !planLoading && showPlanControls && (
                <div className="bg-mp-surface rounded-xl p-6 mt-6">
                    <p className="text-sm text-mp-muted">Loading the plan settings...</p>
                </div>
            )}

            {confirmed && !isContextLoading && !context && showPlanControls && (
                <QueryErrorAlert
                    className="mt-6"
                    message="The plan settings could not be loaded, so the duration limits for this product are unknown. The existing schedule is unaffected."
                    onRetry={() => refetchContext()}
                />
            )}

            {confirmed && !retrievingPlan && !showPlanControls && (
                <SmriqPlanControlsSummary
                    className="mt-4"
                    durationMonths={smriqPlanSettings?.duration_months}
                    includeMotTests={smriqPlanSettings?.include_mot_tests}
                    planStartDate={smriqPlanSettings?.plan_start_date}
                    onEdit={journey.requestPlanEdit}
                />
            )}

            {confirmed && context && !retrievingPlan && showPlanControls && (
                <SmriqPlanControls
                    className="mt-4"
                    durationMin={context.duration.min}
                    durationMax={context.duration.max}
                    // The saved plan's own settings pre-fill the form on a resume; otherwise the
                    // product's configured defaults do.
                    durationDefault={smriqPlanSettings?.duration_months ?? context.duration.default}
                    durationOptions={context.duration.options}
                    motDefault={smriqPlanSettings ? smriqPlanSettings.include_mot_tests : context.mot.default}
                    allowMotSelection={context.mot.allow_selection}
                    planStartDateDefault={smriqPlanSettings?.plan_start_date}
                    hasPlan={hasPlan}
                    isStale={isStale}
                    isGenerating={isGenerating}
                    generateError={generateError}
                    needsReconfirm={needsReconfirm}
                    planSettingsUnknown={hasPlan && !planSettingsKnownOrRestored}
                    canGeneratePlan={lastCompleted.canGeneratePlan}
                    selectionValid={lastCompleted.selectionValid}
                    lastCompletedServiceInPanel={lastCompletedInPanel}
                    onGenerate={journey.handleGenerate}
                    onFieldsChange={journey.handlePlanFieldsChange}
                    onPlanStartDateChange={journey.handlePlanStartDateChange}
                    onCancel={journey.cancelPlanEdit}
                    onRestart={journey.handleRestart}
                />
            )}

            <SmriqSchedulePanel
                className="mt-4"
                services={configuredServices}
                currency={currency}
                inclusionsByVisit={journey.inclusionsByVisit}
                isItemDeclined={journey.isItemDeclined}
                onToggleDecline={journey.handleToggleDecline}
                isDeclineAllowed={journey.isDeclineAllowed}
                atExclusionLimit={journey.atExclusionLimit}
                exclusionsSupported={journey.exclusionsSupported}
                disableToggles={journey.disableToggles}
                loading={planLoading}
                pricingVariantDescription={journey.pricingVariantDescription}
                errors={serviceOrderErrors}
            />
        </div>
    );
};
